import { Model } from '@nozbe/watermelondb'
import { field, date, readonly, relation } from '@nozbe/watermelondb/decorators'

// ═══════════════════════════════════════
// PRICING & PROMOTIONS
// ═══════════════════════════════════════

export class TaxProfile extends Model {
    static table = 'tax_profiles'
    @field('merchant_id') merchantId!: string
    @field('name') name!: string
    @field('tax_type') taxType!: string
    @field('rate') rate!: number
    @field('cess_rate') cessRate!: number
    @field('is_inter_state') isInterState!: boolean
    @field('is_default') isDefault!: boolean
    @field('version') version!: number
    @field('is_synced') isSynced!: boolean
    @field('is_deleted') isDeleted!: boolean
    @readonly @date('created_at') createdAt!: Date
    @readonly @date('updated_at') updatedAt!: Date
}

export class PriceList extends Model {
    static table = 'price_lists'
    @field('merchant_id') merchantId!: string
    @field('name') name!: string
    @field('is_active') isActive!: boolean
    @field('version') version!: number
    @field('is_synced') isSynced!: boolean
    @field('is_deleted') isDeleted!: boolean
    @readonly @date('created_at') createdAt!: Date
    @readonly @date('updated_at') updatedAt!: Date
}

export class PriceListItem extends Model {
    static table = 'price_list_items'
    @field('price_list_id') priceListId!: string
    @field('product_id') productId!: string
    @field('price') price!: number
    @field('min_quantity') minQuantity!: number
    @field('version') version!: number
    @field('is_synced') isSynced!: boolean
    @field('is_deleted') isDeleted!: boolean
    @readonly @date('created_at') createdAt!: Date
    @readonly @date('updated_at') updatedAt!: Date
}

export class Promotion extends Model {
    static table = 'promotions'
    @field('merchant_id') merchantId!: string
    @field('name') name!: string
    @field('code') code!: string | null
    @field('type') type!: string
    @field('value') value!: number
    @field('min_order_value') minOrderValue!: number | null
    @field('max_discount') maxDiscount!: number | null
    @field('applicable_to') applicableTo!: string
    @field('applicable_ids') applicableIds!: string | null
    @date('start_date') startDate!: Date
    @date('end_date') endDate!: Date
    @field('usage_limit') usageLimit!: number | null
    @field('used_count') usedCount!: number
    @field('is_active') isActive!: boolean
    @field('version') version!: number
    @field('is_synced') isSynced!: boolean
    @field('is_deleted') isDeleted!: boolean
    @field('last_sync_used_count') lastSyncUsedCount!: number | null
    @readonly @date('created_at') createdAt!: Date
    @readonly @date('updated_at') updatedAt!: Date
}

// ═══════════════════════════════════════
// GST & COMPLIANCE
// ═══════════════════════════════════════

export class GstRateSlab extends Model {
    static table = 'gst_rate_slabs'
    @field('rate') rate!: number
    @field('label') label!: string
    @field('is_active') isActive!: boolean
    @date('effective_from') effectiveFrom!: Date
    @field('sort_order') sortOrder!: number
    @field('version') version!: number
    @field('is_synced') isSynced!: boolean
    @field('is_deleted') isDeleted!: boolean
    @readonly @date('created_at') createdAt!: Date
    @readonly @date('updated_at') updatedAt!: Date
}

export class IndianState extends Model {
    static table = 'indian_states'
    @field('code') code!: string
    @field('name') name!: string
    @field('short_name') shortName!: string
    @field('is_ut') isUt!: boolean
    @field('is_active') isActive!: boolean
    @field('version') version!: number
    @field('is_synced') isSynced!: boolean
    @field('is_deleted') isDeleted!: boolean
    @readonly @date('created_at') createdAt!: Date
    @readonly @date('updated_at') updatedAt!: Date
}

export class HsnMaster extends Model {
    static table = 'hsn_master'
    @field('code') code!: string
    @field('description') description!: string
    @field('gst_rate') gstRate!: number
    @field('cess_rate') cessRate!: number
    @field('keywords') keywords!: string | null
    @readonly @date('created_at') createdAt!: Date
    @readonly @date('updated_at') updatedAt!: Date
}

export class GstFiling extends Model {
    static table = 'gst_filings'
    @field('store_id') storeId!: string
    @field('return_type') returnType!: string
    @field('period') period!: string
    @field('status') status!: string
    @field('total_taxable') totalTaxable!: number | null
    @field('total_tax') totalTax!: number | null
    @field('filing_data') filingData!: string | null
    @date('filed_at') filedAt!: Date | null
    @field('ack_number') ackNumber!: string | null
    @field('version') version!: number
    @field('is_synced') isSynced!: boolean
    @field('is_deleted') isDeleted!: boolean
    @readonly @date('created_at') createdAt!: Date
    @readonly @date('updated_at') updatedAt!: Date
}

export class EInvoice extends Model {
    static table = 'e_invoices'
    static associations = {
        invoices: { type: 'belongs_to', key: 'invoice_id' },
    } as const
    @field('invoice_id') invoiceId!: string
    @field('irn') irn!: string | null
    @field('ack_number') ackNumber!: string | null
    @date('ack_date') ackDate!: Date | null
    @field('signed_qr') signedQr!: string | null
    @field('signed_invoice') signedInvoice!: string | null
    @field('status') status!: string
    @field('error_message') errorMessage!: string | null
    @field('version') version!: number
    @field('is_synced') isSynced!: boolean
    @field('is_deleted') isDeleted!: boolean
    @readonly @date('created_at') createdAt!: Date
    @readonly @date('updated_at') updatedAt!: Date
    
    @relation('invoices', 'invoice_id') invoice: any
}

export class EWayBill extends Model {
    static table = 'e_way_bills'
    @field('invoice_id') invoiceId!: string
    @field('ewb_number') ewbNumber!: string | null
    @field('from_state') fromState!: string
    @field('to_state') toState!: string
    @field('trans_mode') transMode!: string | null
    @field('vehicle_number') vehicleNumber!: string | null
    @date('valid_upto') validUpto!: Date | null
    @field('status') status!: string
    @field('error_message') errorMessage!: string | null
    @field('version') version!: number
    @field('is_synced') isSynced!: boolean
    @field('is_deleted') isDeleted!: boolean
    @readonly @date('created_at') createdAt!: Date
    @readonly @date('updated_at') updatedAt!: Date
}

// ═══════════════════════════════════════
// STAFF MANAGEMENT
// ═══════════════════════════════════════

export class StaffAssignment extends Model {
    static table = 'staff_assignments'
    @field('user_id') userId!: string
    @field('store_id') storeId!: string
    @field('permissions') permissions!: string | null
    @field('commission_rate') commissionRate!: number
    @field('salary') salary!: number
    @field('version') version!: number
    @field('is_synced') isSynced!: boolean
    @field('is_deleted') isDeleted!: boolean
    @readonly @date('created_at') createdAt!: Date
    @readonly @date('updated_at') updatedAt!: Date
}

export class Shift extends Model {
    static table = 'shifts'
    @field('store_id') storeId!: string
    @field('name') name!: string
    @field('start_time') startTime!: string
    @field('end_time') endTime!: string
    @field('version') version!: number
    @field('is_synced') isSynced!: boolean
    @field('is_deleted') isDeleted!: boolean
    @readonly @date('created_at') createdAt!: Date
    @readonly @date('updated_at') updatedAt!: Date
}

export class Attendance extends Model {
    static table = 'attendance'
    @field('user_id') userId!: string
    @field('store_id') storeId!: string
    @field('shift_id') shiftId!: string | null
    @date('date') date!: Date
    @date('check_in') checkIn!: Date | null
    @date('check_out') checkOut!: Date | null
    @field('status') status!: string
    @field('hours_worked') hoursWorked!: number | null
    @field('notes') notes!: string | null
    @field('version') version!: number
    @field('is_synced') isSynced!: boolean
    @field('is_deleted') isDeleted!: boolean
    @readonly @date('created_at') createdAt!: Date
    @readonly @date('updated_at') updatedAt!: Date
}

export class Commission extends Model {
    static table = 'commissions'
    @field('user_id') userId!: string
    @field('invoice_id') invoiceId!: string
    @field('amount') amount!: number
    @field('rate') rate!: number
    @field('status') status!: string
    @field('version') version!: number
    @field('is_synced') isSynced!: boolean
    @field('is_deleted') isDeleted!: boolean
    @readonly @date('created_at') createdAt!: Date
    @readonly @date('updated_at') updatedAt!: Date
}

// ═══════════════════════════════════════
// FINANCE & EXPENSES
// ═══════════════════════════════════════

export class ExpenseCategory extends Model {
    static table = 'expense_categories'
    @field('merchant_id') merchantId!: string
    @field('name') name!: string
    @field('version') version!: number
    @field('is_synced') isSynced!: boolean
    @field('is_deleted') isDeleted!: boolean
    @readonly @date('created_at') createdAt!: Date
    @readonly @date('updated_at') updatedAt!: Date
}

export class Expense extends Model {
    static table = 'expenses'
    @field('store_id') storeId!: string
    @field('category_id') categoryId!: string
    @field('amount') amount!: number
    @field('payment_method') paymentMethod!: string | null
    @field('description') description!: string | null
    @date('expense_date') expenseDate!: Date
    @field('receipt_url') receiptUrl!: string | null
    /** The drawer this cash left. Null on rows written before v24/v25. */
    @field('billing_session_id') billingSessionId!: string | null
    @field('version') version!: number
    @field('is_synced') isSynced!: boolean
    @field('is_deleted') isDeleted!: boolean
    @readonly @date('created_at') createdAt!: Date
    @readonly @date('updated_at') updatedAt!: Date
}

// ═══════════════════════════════════════
// SYSTEM & DEVICES
// ═══════════════════════════════════════

export class Device extends Model {
    static table = 'devices'
    @field('merchant_id') merchantId!: string
    @field('user_id') userId!: string | null
    @field('device_name') deviceName!: string | null
    @field('platform') platform!: string | null
    @field('app_version') appVersion!: string | null
    @field('push_token') pushToken!: string | null
    @date('last_sync_at') lastSyncAt!: Date | null
    @field('store_id') storeId!: string | null
    @field('device_slot') deviceSlot!: number | null
    @field('is_active') isActive!: boolean
    @field('is_wiped') isWiped!: boolean
    @field('pin_hash') pinHash!: string | null
    @field('device_type') deviceType!: string
    @readonly @date('created_at') createdAt!: Date
    @readonly @date('updated_at') updatedAt!: Date
}

export class AuditLog extends Model {
    static table = 'audit_logs'
    @field('merchant_id') merchantId!: string | null
    @field('user_id') userId!: string | null
    @field('admin_user_id') adminUserId!: string | null
    @field('entity_type') entityType!: string
    @field('entity_id') entityId!: string
    @field('action') action!: string
    @field('changes') changes!: string | null
    @field('ip_address') ipAddress!: string | null
    @readonly @date('created_at') createdAt!: Date
}

export class Setting extends Model {
    static table = 'settings'
    static associations = {
        stores: { type: 'belongs_to', key: 'store_id' },
        merchants: { type: 'belongs_to', key: 'merchant_id' },
    } as const
    @field('store_id') storeId!: string | null
    @field('merchant_id') merchantId!: string
    @field('category') category!: string
    @field('setting_key') settingKey!: string
    @field('setting_value') settingValue!: string
    @field('version') version!: number
    @field('is_synced') isSynced!: boolean
    @field('is_deleted') isDeleted!: boolean
    @readonly @date('created_at') createdAt!: Date
    @readonly @date('updated_at') updatedAt!: Date

    @relation('stores', 'store_id') store: any
    @relation('merchants', 'merchant_id') merchant: any
}

export class SyncLogEntry extends Model {
    static table = 'sync_logs'
    @field('merchant_id') merchantId!: string | null
    @field('device_id') deviceId!: string
    @field('direction') direction!: string
    @field('table_name') tableName!: string
    @field('record_count') recordCount!: number
    @field('status') status!: string
    @field('error_message') errorMessage!: string | null
    @field('duration_ms') durationMs!: number | null
    @readonly @date('created_at') createdAt!: Date
}

export class Notification extends Model {
    static table = 'notifications'
    @field('merchant_id') merchantId!: string
    @field('store_id') storeId!: string | null
    @field('user_id') userId!: string | null
    @field('type') type!: string
    @field('title') title!: string
    @field('body') body!: string
    @field('data') data!: string | null
    @field('is_read') isRead!: boolean
    @field('version') version!: number
    @field('is_synced') isSynced!: boolean
    @field('is_deleted') isDeleted!: boolean
    @readonly @date('created_at') createdAt!: Date
    @readonly @date('updated_at') updatedAt!: Date
}

// ═══════════════════════════════════════
// AI / CACHE
// ═══════════════════════════════════════

export class HsnCache extends Model {
    static table = 'hsn_cache'
    @field('product_query') productQuery!: string
    @field('hsn_code') hsnCode!: string
    @field('description') description!: string
    @field('gst_rate') gstRate!: number
    @field('confidence') confidence!: string
    @field('alternatives_json') alternativesJson!: string
    @field('note') note!: string | null
    @field('source') source!: string
    @field('hit_count') hitCount!: number
    @readonly @date('created_at') createdAt!: Date
    @readonly @date('updated_at') updatedAt!: Date
}

export class ReorderSuggestion extends Model {
    static table = 'reorder_suggestions'
    @field('store_id') storeId!: string
    @field('merchant_id') merchantId!: string
    @date('generated_at') generatedAt!: Date
    @field('urgent_json') urgentJson!: string
    @field('plan_json') planJson!: string
    @field('reduce_json') reduceJson!: string
    @field('seasonal_tip') seasonalTip!: string | null
    @field('is_read') isRead!: boolean
    @readonly @date('updated_at') updatedAt!: Date
}
